import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
} from 'chart.js';
import React, { useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { brandText } from '../../../../../config/branding';
import currentTimeZone from '../../../../../enum/timeZone';
import { useAppSelector } from '../../../../../redux/hooks';
import BatteryLoader from '../../../../../shared-components/battery-loader/BatteryLoader';
import CustomDateRangePicker from '../../../../../shared-components/custom-date-range/CustomDateRangePicker';
import ToFixedValue from '../../../../../shared-functions/ToFixedValue';
import device from '../../../../../utils/api/device';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

interface ChartDataProperties {
  date?: string;
  hour?: number | string;
  time?: string;
  label?: string;
  totalPowerConsumed?: number;
  energyDelivered?: number;
  totalRevenue?: number;
  totalDebitAmount?: number;
  cpoRevenueShare?: number;
  partnerRevenueShare?: number;
  sessionCount?: number;
}

/**
 * Formats a chart label based on whether it represents an hour or a specific date.
 * @param {ChartDataProperties} item - The chart data item.
 * @param {string} [locale] - Current active locale.
 * @param {string} [startDate] - Optional start date of current range.
 * @param {string} [endDate] - Optional end date of current range.
 * @returns {string} Formatted label string for the chart axis.
 */
const formatChartLabel = (
  item: ChartDataProperties,
  locale: string = 'en',
  startDate?: string,
  endDate?: string,
): string => {
  // 1. Hour / Time format handling (e.g. { hour: 14, time: "14:00", date: "2026-08-31" })
  if (item.time !== undefined && item.time !== null && item.time !== '') {
    return String(item.time).trim().slice(0, 5);
  }
  if (item.hour !== undefined && item.hour !== null && item.hour !== '') {
    const hourNumber = Number(item.hour);
    if (!Number.isNaN(hourNumber)) {
      return `${String(hourNumber).padStart(2, '0')}:00`;
    }
    return String(item.hour).trim();
  }

  // 2. Specific Date / Day handling
  let parsedDate: Date | null = null;
  const rawDate = item.date;

  if (rawDate !== undefined && rawDate !== null && rawDate !== '') {
    const dateString = String(rawDate).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(dateString)) {
      const [yearString, monthPart, dayString] = dateString.split('T')[0].split('-');
      const dateObject = new Date(Number(yearString), Number(monthPart) - 1, Number(dayString));
      if (!Number.isNaN(dateObject.getTime())) {
        parsedDate = dateObject;
      }
    } else if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
      const [dayString, monthPart, yearString] = dateString.split('-');
      const dateObject = new Date(Number(yearString), Number(monthPart) - 1, Number(dayString));
      if (!Number.isNaN(dateObject.getTime())) {
        parsedDate = dateObject;
      }
    } else if (dateString.includes('-') || dateString.includes('/')) {
      const generalDate = new Date(dateString);
      if (!Number.isNaN(generalDate.getTime())) {
        parsedDate = generalDate;
      }
    }
  }

  if (parsedDate) {
    const isMultiYear =
      Boolean(startDate) &&
      Boolean(endDate) &&
      new Date(startDate!).getFullYear() !== new Date(endDate!).getFullYear();

    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: 'short',
      ...(isMultiYear ? { year: '2-digit' } : {}),
    }).format(parsedDate);
  }

  return item.label || '';
};

/**
 * Formats a detailed title string for the chart tooltip.
 * @param {ChartDataProperties} item - The chart data item.
 * @param {string} [locale] - Current active locale.
 * @returns {string} Formatted tooltip title string.
 */
const getTooltipTitle = (item: ChartDataProperties, locale: string = 'en'): string => {
  // 1. Time / Hour with date
  if (item.date && item.time) {
    return `${item.date} ${item.time}`;
  }
  if (item.date && item.hour !== undefined && item.hour !== null && item.hour !== '') {
    const hourNumber = Number(item.hour);
    const hourPad = !Number.isNaN(hourNumber)
      ? `${String(hourNumber).padStart(2, '0')}:00`
      : String(item.hour);
    return `${item.date} ${hourPad}`;
  }

  // 2. Specific date
  if (item.date && /^\d{4}-\d{2}-\d{2}/.test(String(item.date))) {
    const [yearString, monthPart, dayString] = String(item.date).split('T')[0].split('-');
    const dateObject = new Date(Number(yearString), Number(monthPart) - 1, Number(dayString));
    if (!Number.isNaN(dateObject.getTime())) {
      return new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }).format(dateObject);
    }
  }

  return item.label || '';
};

/**
 * Parses a date string in YYYY-MM-DD format into year, month, and day numbers.
 * @param {string} dateString - Date string to parse.
 * @returns {{ year: number; month: number; day: number } | null} Parsed components or null if invalid.
 */
const parseDateParts = (
  dateString: string,
): { year: number; month: number; day: number } | null => {
  if (!dateString) return null;
  const clean = dateString.split('T')[0].trim();
  const parts = clean.split('-');
  if (parts.length === 3) {
    const year = Number(parts[0]);
    const month = Number(parts[1]);
    const day = Number(parts[2]);
    if (!Number.isNaN(year) && !Number.isNaN(month) && !Number.isNaN(day)) {
      return { year, month, day };
    }
  }
  return null;
};

/**
 * Normalizes and fills missing intervals for hourly or daily chart data.
 * @param {any[]} rawList - The raw data array returned from the API.
 * @param {string} startDate - Selected query start date (YYYY-MM-DD).
 * @param {string} endDate - Selected query end date (YYYY-MM-DD).
 * @param {string} locale - Current active locale.
 * @returns {ChartDataProperties[]} Complete list of data points with missing periods filled with 0.
 */
const buildCompleteChartData = (
  rawList: any[],
  startDate: string,
  endDate: string,
  locale: string,
): ChartDataProperties[] => {
  const hasHour = rawList.some(
    (item: any) =>
      (item.hour !== undefined && item.hour !== null && item.hour !== '') ||
      (item.time !== undefined && item.time !== null && item.time !== ''),
  );

  const isHourly = hasHour || Boolean(startDate && endDate && startDate === endDate);

  if (isHourly) {
    const result: ChartDataProperties[] = [];
    for (let h = 0; h < 24; h += 1) {
      const hourPad = String(h).padStart(2, '0');
      const timeLabel = `${hourPad}:00`;

      const matched = rawList.find((item: any) => {
        if (item.hour !== undefined && item.hour !== null && item.hour !== '') {
          const itemHour = Number(item.hour);
          if (itemHour === h) return true;
          if (h === 0 && itemHour === 24) return true;
        }
        if (item.time !== undefined && item.time !== null && item.time !== '') {
          const timeString = String(item.time).trim();
          const hourPart = Number(timeString.split(':')[0]);
          if (!Number.isNaN(hourPart) && hourPart === h) return true;
        }
        return false;
      });

      const powerValue = Number(matched?.totalPowerConsumed ?? matched?.energyDelivered ?? 0);
      const revenueValue = Number(matched?.totalRevenue ?? matched?.totalDebitAmount ?? 0);

      result.push({
        ...(matched || {}),
        hour: h,
        time: timeLabel,
        date: matched?.date || startDate,
        totalPowerConsumed: powerValue,
        energyDelivered: Number(matched?.energyDelivered ?? powerValue),
        totalRevenue: revenueValue,
        totalDebitAmount: Number(matched?.totalDebitAmount ?? revenueValue),
        sessionCount:
          matched?.sessionCount !== undefined ? Number(matched.sessionCount) : undefined,
        label: timeLabel,
      });
    }
    return result;
  }

  const startParts = parseDateParts(startDate);
  const endParts = parseDateParts(endDate);

  if (startParts && endParts) {
    const startDateObject = new Date(startParts.year, startParts.month - 1, startParts.day);
    const endDateObject = new Date(endParts.year, endParts.month - 1, endParts.day);

    if (startDateObject <= endDateObject) {
      const result: ChartDataProperties[] = [];
      const currentDate = new Date(startDateObject);

      while (currentDate <= endDateObject) {
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth() + 1;
        const currentDay = currentDate.getDate();
        const isoDate = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(currentDay).padStart(2, '0')}`;

        const matched = rawList.find((item: any) => {
          if (!item.date) return false;
          const itemDate = String(item.date).split('T')[0].trim();
          return itemDate === isoDate;
        });

        const powerValue = Number(matched?.totalPowerConsumed ?? matched?.energyDelivered ?? 0);
        const revenueValue = Number(matched?.totalRevenue ?? matched?.totalDebitAmount ?? 0);
        const isMultiYear =
          Boolean(startDate) &&
          Boolean(endDate) &&
          new Date(startDate).getFullYear() !== new Date(endDate).getFullYear();

        const labelDate = new Intl.DateTimeFormat(locale, {
          day: '2-digit',
          month: 'short',
          ...(isMultiYear ? { year: '2-digit' } : {}),
        }).format(currentDate);

        result.push({
          ...(matched || {}),
          date: isoDate,
          totalPowerConsumed: powerValue,
          energyDelivered: Number(matched?.energyDelivered ?? powerValue),
          totalRevenue: revenueValue,
          totalDebitAmount: Number(matched?.totalDebitAmount ?? revenueValue),
          sessionCount:
            matched?.sessionCount !== undefined ? Number(matched.sessionCount) : undefined,
          label: labelDate,
        });

        currentDate.setDate(currentDate.getDate() + 1);
      }
      return result;
    }
  }

  const fallbackList = rawList.map((item: any) => {
    const powerValue = Number(item.totalPowerConsumed ?? item.energyDelivered ?? 0);
    const revenueValue = Number(item.totalRevenue ?? item.totalDebitAmount ?? 0);
    return {
      ...item,
      totalPowerConsumed: powerValue,
      energyDelivered: Number(item.energyDelivered ?? powerValue),
      totalRevenue: revenueValue,
      totalDebitAmount: Number(item.totalDebitAmount ?? revenueValue),
      label: formatChartLabel(item, locale, startDate, endDate),
    };
  });

  fallbackList.sort((a: any, b: any) => {
    const dateA = a.date ? new Date(a.date).getTime() : NaN;
    const dateB = b.date ? new Date(b.date).getTime() : NaN;
    if (!Number.isNaN(dateA) && !Number.isNaN(dateB) && dateA !== dateB) {
      return dateA - dateB;
    }
    if (a.day !== undefined && b.day !== undefined && a.day !== b.day) {
      return Number(a.day) - Number(b.day);
    }
    if (a.hour !== undefined && b.hour !== undefined && a.hour !== b.hour) {
      return Number(a.hour) - Number(b.hour);
    }
    return 0;
  });

  return fallbackList;
};

/**
 * Component to display transaction charts
 * @returns {React.ReactElement} The rendered component
 */
function TransactionCharts(): JSX.Element {
  const { t, i18n } = useTranslation(['devices', 'report', 'sidePanel']);
  const { id } = useParams<{ id: string }>();
  const { selectedChargingPoint } = useAppSelector((state) => state.device);

  const chargingPointSn =
    selectedChargingPoint?.chargingPointSn || selectedChargingPoint?.name || id;

  const [powerConsumedRevenue, setPowerConsumedRevenue] = useState<ChartDataProperties[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: '',
  });

  /** Draws the vertical crosshair line using the theme's success color on hover */
  const verticalHoverLinePlugin = {
    id: 'verticalHoverLine',
    /**
     * Draws a success-colored vertical crosshair line at the hovered x position.
     * @param {any} chart - The Chart.js instance
     */
    afterDraw(chart: any) {
      if (chart.tooltip?._active?.length) {
        const context = chart.ctx as CanvasRenderingContext2D;
        const { x } = chart.tooltip._active[0].element;
        const { top, bottom } = chart.chartArea;

        const successColor = getComputedStyle(document.documentElement)
          .getPropertyValue('--success')
          .trim();

        context.save();
        context.beginPath();
        context.moveTo(x, top);
        context.lineTo(x, bottom);
        context.lineWidth = 1;
        context.strokeStyle = successColor || '#2E9E3A';
        context.stroke();
        context.restore();
      }
    },
  };

  const revenueChartData = useMemo(
    () => ({
      labels: powerConsumedRevenue.map((eachData) => eachData.label),
      datasets: [
        {
          label: t('revenue', { defaultValue: 'Revenue' }),
          data: powerConsumedRevenue.map((d) =>
            ToFixedValue(d.totalRevenue ?? d.totalDebitAmount ?? 0, 2),
          ),
          borderColor: '#F79009',
          /**
           * Background gradient generator
           * @param {any} context - The dataset context
           * @returns {any} The gradient background
           */
          backgroundColor: (context: any) => {
            const { chart } = context;
            const contentContext = chart.ctx;
            const gradient = contentContext.createLinearGradient(0, 0, 0, chart.height);
            gradient.addColorStop(0, 'rgba(247, 144, 9, 0.4)');
            gradient.addColorStop(1, 'rgba(237, 237, 237, 0)');
            return gradient;
          },
          fill: 'origin',
          tension: 0.4,
          borderWidth: 1,
          pointRadius: powerConsumedRevenue.length > 25 ? 0 : 3,
          pointHoverRadius: 6,
        },
      ],
    }),
    [powerConsumedRevenue, t],
  );

  const powerConsumedChartData = useMemo(
    () => ({
      labels: powerConsumedRevenue.map((eachData) => eachData.label),
      datasets: [
        {
          label: t('powerConsumed', { defaultValue: 'Power Consumed' }),
          data: powerConsumedRevenue.map((d) =>
            ToFixedValue(d.totalPowerConsumed ?? d.energyDelivered ?? 0, 3),
          ),
          borderColor: '#2E9E3A',
          /**
           * Function to create a gradient bg color
           * @param {any} context - The context
           * @returns {any} The result
           */
          backgroundColor: (context: any) => {
            const { chart } = context;
            const chartContext = chart.ctx;
            const gradient = chartContext.createLinearGradient(0, 0, 1, chart.height);
            gradient.addColorStop(0, 'rgba(46, 158, 58, 1)');
            gradient.addColorStop(1, 'rgba(237, 237, 237, 0)');
            return gradient;
          },
          fill: 'origin',
          tension: 0.4,
          borderWidth: 1,
          pointRadius: powerConsumedRevenue.length > 25 ? 0 : 3,
          pointHoverRadius: 6,
        },
      ],
    }),
    [powerConsumedRevenue, t],
  );

  const options: ChartOptions<'line'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
          position: 'top' as const,
          align: 'end' as const,
          labels: {
            usePointStyle: true,
            boxWidth: 10,
            boxHeight: 10,
            borderRadius: 10,
            padding: 15,
          },
        },
        tooltip: {
          backgroundColor: '#1F2937',
          titleColor: '#FFFFFF',
          bodyColor: '#FFFFFF',
          padding: 10,
          borderRadius: 8,
          cornerRadius: 4,
          displayColors: true,
          rtl: i18n.language === 'ar',
          textDirection: i18n.language === 'ar' ? 'rtl' : 'ltr',
          callbacks: {
            /**
             * Function to generate title for chart tooltip
             * @param {any} tooltipItems - Tooltip items
             * @returns {string} The tooltip title
             */
            title: (tooltipItems: any): string => {
              const index = tooltipItems[0]?.dataIndex;
              const item = powerConsumedRevenue[index];
              if (!item) return '';
              const formattedTitle = getTooltipTitle(item, i18n.language);
              if (formattedTitle) {
                return formattedTitle;
              }
              return item.label || '';
            },
            /**
             * Function to show tooltip label data
             * @param {any} context - The context
             * @returns {string} The label string
             */
            label: (context: any): string => {
              const datasetLabel = context?.dataset?.label || '';
              const rawValue = Number(context?.raw ?? 0);
              const isRevenue = datasetLabel === t('revenue', { defaultValue: 'Revenue' });
              const { currency } = brandText;
              const unit = isRevenue ? currency : 'kWh';
              return `${datasetLabel}: ${ToFixedValue(rawValue, isRevenue ? 2 : 3)} ${unit}`;
            },
            /**
             * Function to show additional tooltip data
             * @param {any} context - The context
             * @returns {string} The afterLabel string
             */
            afterLabel: (context: any): string => {
              const index = context?.dataIndex;
              const item = powerConsumedRevenue[index];
              if (item?.sessionCount !== undefined && item?.sessionCount !== null) {
                const sessionCountLabel = t('sessionCount', {
                  defaultValue: t('report:sessionCount', { defaultValue: 'Session Count' }),
                });
                return `${sessionCountLabel}: ${item.sessionCount}`;
              }
              return '';
            },
          },
        },
      },
      interaction: {
        intersect: false,
        mode: 'index' as const,
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          border: {
            display: false,
          },
          ticks: {
            display: true,
            autoSkip: true,
            maxTicksLimit: 12,
            maxRotation: 45,
            minRotation: 0,
            color: '#6b7280',
            font: {
              size: 11,
              weight: 500,
            },
          },
        },
        y: {
          grid: {
            display: false,
          },
          ticks: {
            display: false,
          },
          border: {
            display: false,
          },
        },
      },
    }),
    [t, i18n.language, powerConsumedRevenue],
  );

  /**
   * Function to fetch the transaction report.
   * @param {string} startDate - Report start date.
   * @param {string} endDate - Report end date.
   * @returns {Promise<void>} Resolves when data is fetched.
   */
  const handleFetchTransactionReport = async (
    startDate: string,
    endDate: string,
  ): Promise<void> => {
    if (!chargingPointSn || !startDate || !endDate) {
      return;
    }
    setLoading(true);
    try {
      const response = await device.fetchTransactionReport({
        chargingPointSn,
        timeZone: currentTimeZone,
        startDate,
        endDate,
      });

      let rawList: any[] = [];
      if (Array.isArray(response?.data)) {
        rawList = response.data;
      } else if (Array.isArray(response?.data?.data)) {
        rawList = response.data.data;
      } else if (Array.isArray(response?.data?.results)) {
        rawList = response.data.results;
      } else if (Array.isArray(response?.data?.result)) {
        rawList = response.data.result;
      }

      // Normalize raw items: extract Mongo _id subfields (day, year) if present
      const normalizedList = rawList.map((item: any) => {
        const idObject = item._id && typeof item._id === 'object' ? item._id : {};
        const day = item.day ?? idObject.day;
        const year = item.year ?? idObject.year;

        let derivedDate = item.date;
        if (!derivedDate && typeof item._id === 'string' && /^\d{4}-\d{2}-\d{2}/.test(item._id)) {
          derivedDate = item._id;
        }
        if (!derivedDate && day && year) {
          const monthNumber = new Date(startDate || Date.now()).getMonth() + 1;
          const m = String(monthNumber).padStart(2, '0');
          const d = String(day).padStart(2, '0');
          derivedDate = `${year}-${m}-${d}`;
        }

        return {
          ...item,
          day,
          year,
          date: derivedDate || item.date || item.createdAt || '',
        };
      });

      const mappedData = buildCompleteChartData(
        normalizedList,
        startDate,
        endDate,
        i18n.language,
      );

      setPowerConsumedRevenue(mappedData);
    } catch (error) {
      console.error(error);
      setPowerConsumedRevenue([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (chargingPointSn && dateRange.startDate && dateRange.endDate) {
      handleFetchTransactionReport(dateRange.startDate, dateRange.endDate);
    }
  }, [chargingPointSn, dateRange.startDate, dateRange.endDate, i18n.language]);
  return (
    <div className="device-details-container">
      <CustomDateRangePicker
        startDate={dateRange.startDate}
        endDate={dateRange.endDate}
        placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
        defaultPreset="last14Days"
        onChange={(start: string, end: string) => {
          setDateRange({
            startDate: start,
            endDate: end,
          });
        }}
      />
      <div className="charts-row-container">
        <div className="chart-container">
          <h6>{t('powerConsumed', { defaultValue: 'Power Consumed' })}</h6>
          {loading ? (
            <div className="chart-loader-container">
              <BatteryLoader />
            </div>
          ) : (
            <div className="chart-canvas-wrapper">
              <Line
                data={powerConsumedChartData}
                options={options}
                plugins={[verticalHoverLinePlugin]}
              />
            </div>
          )}
        </div>
        <div className="chart-container">
          <h6>{t('revenueGraph', { defaultValue: 'Revenue Graph' })}</h6>
          {loading ? (
            <div className="chart-loader-container">
              <BatteryLoader />
            </div>
          ) : (
            <div className="chart-canvas-wrapper">
              <Line
                data={revenueChartData}
                options={options}
                plugins={[verticalHoverLinePlugin]}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TransactionCharts;